import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAccount, useChainId, useSwitchChain, usePublicClient, useWriteContract } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { parseUnits, formatUnits } from 'viem';
import { PRESALE_ADDRESS, USDT_ADDRESS, PRESALE_ABI, ERC20_ABI, USDT_DECIMALS, AIML_DECIMALS } from '../lib/contracts';
import { api } from '../api/client';

const POLYGON = 137;
const QUICK = [100, 300, 500, 1000];

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

type Step = 'form' | 'review' | 'done';

export default function Buy() {
  const qc = useQueryClient();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const [amount, setAmount] = useState('100');
  const [step, setStep] = useState<Step>('form');
  const [quote, setQuote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const usdt = Number(amount) || 0;

  // Live quote from the contract: how much AIML for this USDT amount.
  // Live quote from the contract: how much AIML for this USDT amount.
  const [quoteError, setQuoteError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setQuote(null);
    setQuoteError(null);
    if (!publicClient || usdt <= 0) return;
    const t = setTimeout(async () => {
      try {
        const usdtRaw = parseUnits(String(usdt), USDT_DECIMALS);
        console.log('[quote] calling getTokenAmountUSDT with', usdtRaw.toString());
        const raw = (await publicClient.readContract({
          address: PRESALE_ADDRESS, abi: PRESALE_ABI, functionName: 'getTokenAmountUSDT',
          args: [usdtRaw],
        })) as bigint;
        console.log('[quote] raw result =', raw.toString());
        if (!cancelled) setQuote(raw.toString()); // contract already returns whole AIML tokens
      } catch (e: any) {
        console.error('[quote] failed:', e);
        if (!cancelled) { setQuote(null); setQuoteError(e?.shortMessage || e?.message || 'quote failed'); }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [usdt, publicClient]);

  const aimlEstimate = useMemo(() => (quote ? Math.round(Number(quote)).toLocaleString() : '—'), [quote]);

  async function confirmPurchase() {
    setErr(null);
    if (!isConnected || !address) { openConnectModal?.(); return; }
    setBusy(true);
    try {
      if (chainId !== POLYGON) { setStatusText('Switching to Polygon…'); await switchChainAsync({ chainId: POLYGON }); }
      const usdtRaw = parseUnits(String(usdt), USDT_DECIMALS);

      // 1) Check existing allowance; approve only if needed.
      setStatusText('Checking USDT allowance…');
      const allowance = (await publicClient!.readContract({
        address: USDT_ADDRESS, abi: ERC20_ABI, functionName: 'allowance', args: [address, PRESALE_ADDRESS],
      })) as bigint;

      if (allowance < usdtRaw) {
        setStatusText('Approve USDT in your wallet…');
        const approveHash = await writeContractAsync({
          address: USDT_ADDRESS, abi: ERC20_ABI, functionName: 'approve', args: [PRESALE_ADDRESS, usdtRaw],
        });
        setStatusText('Waiting for approval to confirm…');
        await publicClient!.waitForTransactionReceipt({ hash: approveHash });
      }

      // 2) Buy.
      setStatusText('Confirm purchase in your wallet…');
      const buyHash = await writeContractAsync({
        address: PRESALE_ADDRESS, abi: PRESALE_ABI, functionName: 'buyTokensWithUSDT', args: [usdtRaw],
      });
      setStatusText('Waiting for purchase to confirm…');
      await publicClient!.waitForTransactionReceipt({ hash: buyHash });
      setTxHash(buyHash);

      // 3) Record referral attribution (backend links it to a referrer if a ref code was used).
      const refCode = localStorage.getItem('altcoins.refCode');
      if (refCode) {
        try { await api('/api/referral/attribute', { method: 'POST', body: { referralCode: refCode, txHash: buyHash, walletAddress: address } }); } catch {}
      }

      await Promise.all([
        qc.invalidateQueries({ queryKey: ['referral-me'] }),
        qc.invalidateQueries({ queryKey: ['referral-referees'] }),
      ]);
      setStatusText(null);
      setStep('done');
    } catch (e: any) {
      setErr(e?.shortMessage || e?.message || 'Purchase failed or was rejected.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 pb-2">
      <div>
        <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>PRESALE</div>
        <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Buy $AIML</h1>
      </div>

      {step === 'form' && (
        <>
          <Card className="p-5">
            <div className="text-sm mb-2" style={{ color: '#c4b5fd' }}>You pay (USDT)</div>
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal"
              className="w-full text-3xl font-bold rounded-2xl px-4 py-3 outline-none"
              style={{ fontFamily: '"Space Grotesk", sans-serif', backgroundColor: '#1d1640', border: '1px solid rgba(255,255,255,0.08)', color: 'white' }} />
            <div className="flex gap-2 mt-3">
              {QUICK.map((q) => (
                <button key={q} onClick={() => setAmount(String(q))} className="flex-1 rounded-xl py-2 text-sm font-semibold"
                  style={usdt === q ? { background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', color: 'white' } : { backgroundColor: '#1d1640', color: '#8B82A8' }}>
                  ${q}
                </button>
              ))}
            </div>
          </Card>

          {quoteError && (
            <div className="text-xs px-1" style={{ color: '#F87171' }}>Quote error: {quoteError}</div>
          )}

          {err && <div className="rounded-2xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{err}</div>}

          <Card className="p-5">
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: '#8B82A8' }}>You receive (est.)</span>
              <span className="text-xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: '#34D399' }}>{aimlEstimate} AIML</span>
            </div>
          </Card>

          {err && <div className="rounded-2xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{err}</div>}

          <button onClick={() => (isConnected ? setStep('review') : openConnectModal?.())} disabled={usdt <= 0}
            className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: usdt <= 0 ? 0.5 : 1 }}>
            {isConnected ? 'Review purchase' : 'Connect wallet to buy'}
          </button>
        </>
      )}

      {step === 'review' && (
        <>
          <Card className="p-5 space-y-3">
            {[
              { k: 'You pay', v: `${usdt} USDT` },
              { k: 'You receive (est.)', v: `${aimlEstimate} AIML` },
              { k: 'Network', v: 'Polygon' },
              { k: 'Counts toward rank', v: `+$${usdt} spend` },
            ].map((row) => (
              <div key={row.k} className="flex justify-between text-sm">
                <span style={{ color: '#8B82A8' }}>{row.k}</span>
                <span className="font-semibold">{row.v}</span>
              </div>
            ))}
          </Card>

          <Card className="p-4"><p className="text-xs" style={{ color: '#8B82A8' }}>You’ll approve USDT (first purchase only), then confirm the buy — two prompts in your wallet.</p></Card>

          {err && <div className="rounded-2xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{err}</div>}
          {busy && statusText && <div className="rounded-2xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(139,92,246,0.12)', color: '#c4b5fd' }}>{statusText}</div>}

          <button onClick={confirmPurchase} disabled={busy} className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Processing…' : 'Confirm in wallet'}
          </button>
          {!busy && <button onClick={() => setStep('form')} className="w-full rounded-2xl py-3 text-sm font-semibold" style={{ backgroundColor: '#1d1640', color: '#8B82A8' }}>Back</button>}
        </>
      )}

      {step === 'done' && (
        <>
          <Card className="p-6 text-center">
            <div className="text-5xl mb-2">🎉</div>
            <div className="text-xl font-bold mb-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Purchase complete</div>
            <p className="text-sm mb-4" style={{ color: '#8B82A8' }}>Your AIML has been credited. It’ll reflect on your dashboard shortly.</p>
            {txHash && (
              <button onClick={() => window.open(`https://polygonscan.com/tx/${txHash}`, '_blank')} className="text-xs font-semibold" style={{ color: '#a78bfa', fontFamily: '"JetBrains Mono", monospace' }}>
                {txHash.slice(0, 10)}…{txHash.slice(-8)} · view ›
              </button>
            )}
          </Card>
          <button onClick={() => { setStep('form'); setAmount('100'); setTxHash(null); }} className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>Buy more</button>
        </>
      )}
    </div>
  );
}